import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';

import ROUTES from '../../../../../../core/constants/routes.constant';
import { chartColours } from '../../../../../../core/constants/colours.constants';

interface WithdrawalOptionProps {
  selectedOption?: string;
}

function WithdrawalOption({ selectedOption }: WithdrawalOptionProps) {
  const { t } = useTranslation('portal');

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
      }}
    >
      <Typography
        variant="body1"
        component="p"
        color="text.secondary"
        sx={{
          lineHeight: 1.65,
          maxWidth: 780,
        }}
      >
        {t('preferences.withdraw.para1')}
      </Typography>

      {selectedOption && (
        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: `${chartColours[5]}55`,
            borderRadius: 3,
            bgcolor: `${chartColours[5]}12`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${chartColours[5]}26`,
                color: chartColours[5],
                border: 1,
                borderColor: `${chartColours[5]}55`,
                flexShrink: 0,
              }}
            >
              <InfoOutlined sx={{ fontSize: 24 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                component="p"
                color="text.secondary"
                sx={{
                  mb: 0.75,
                  fontWeight: 800,
                }}
              >
                {t('preferences.withdraw.previously')}:
              </Typography>

              <Box
                sx={{
                  '& p': {
                    m: 0,
                    fontWeight: 900,
                    lineHeight: 1.45,
                  },
                }}
              >
                <Markdown>{t(`withdrawal2.options.${selectedOption}.label`)}</Markdown>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'right',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          variant="contained"
          color="error"
          size="large"
          component={RouterLink}
          data-testid="withdrawal-btn"
          to={ROUTES.portalWithdrawal}
          endIcon={<ArrowForwardOutlined />}
          sx={{
            borderRadius: 999,
            px: 3,
            py: 1.2,
            fontWeight: 900,
            textTransform: 'none',
            boxShadow: 3,
            minWidth: { xs: '100%', sm: 220 },
          }}
        >
          {t('preferences.withdraw.button')}
        </Button>
      </Box>
    </Box>
  );
}

export default WithdrawalOption;
